import { memo, type CSSProperties } from 'react';
import { useSignedAssetUrl } from '../knowledge-base/useSignedAssetUrl';
import type { SlideDraftItem } from './useSlideDraft';
import {
  GRID_CELL_GAP_X,
  GRID_CELL_GAP_Y,
  GRID_NAME_FONT,
  GRID_NARROW_MAX_ITEMS,
  GRID_PORTRAIT_DIAMETER,
  GRID_SUBTITLE_FONT,
  MENU_ROW_GAP,
  MENU_ROW_SUBTITLE_FONT,
  MENU_ROW_TITLE_FONT,
  OFFER_ROW_FONT,
  OFFER_TICK_GAP,
  initials,
  rgb,
} from './slideItemGeometry';

/**
 * Browser approximations of the item layers the renderer draws — the portrait
 * discs, the team grid, the menu rows and the offer's benefit rows.
 *
 * They live *inside* the caption layer, in the renderer's own stack order, so
 * dragging the caption moves the whole composition exactly as a re-render
 * would. Everything here is decoration on a draggable block: no handlers, no
 * focus, and images never start their own drag.
 */

/** Ink that reads on the accent disc — the renderer's chip-colour rule, roughly. */
function inkOn(accent: string): string {
  const [red, green, blue] = rgb(accent);
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
  return luminance > 0.55 ? '#1C1B18' : '#F7F5F0';
}

interface PortraitDiscProps {
  /** Asset path of the photo, or null for the initials placeholder. */
  assetPath: string | null;
  /** The item's title, for the placeholder initials. */
  title: string;
  accent: string;
  /** Disc diameter as a fraction of the slide. */
  diameter: number;
}

/** One circle portrait: the photo circle-cropped, or an accent initials disc. */
export const PortraitDisc = memo(function PortraitDisc({
  assetPath,
  title,
  accent,
  diameter,
}: PortraitDiscProps) {
  const { url } = useSignedAssetUrl(assetPath);
  const style: CSSProperties = {
    width: `${diameter * 100}cqw`,
    height: `${diameter * 100}cqw`,
    borderColor: accent,
  };
  if (url === null) {
    return (
      <span
        className="slide-canvas__portrait slide-canvas__portrait--initials"
        style={{ ...style, background: accent, color: inkOn(accent), fontSize: `${(diameter / 3) * 100}cqw` }}
        aria-hidden="true"
      >
        {initials(title)}
      </span>
    );
  }
  return (
    <span className="slide-canvas__portrait" style={style} aria-hidden="true">
      <img src={url} alt="" draggable={false} />
    </span>
  );
});

interface AnniversaryPhotoProps {
  assetPath: string | null;
  title: string;
  accent: string;
}

/**
 * The anniversary poster's photo, filling its arch container: the photograph
 * cover-fitted, or an accent panel with the initials — the renderer's own
 * placeholder. The arch silhouette is the container's border-radius.
 */
export const AnniversaryPhoto = memo(function AnniversaryPhoto({
  assetPath,
  title,
  accent,
}: AnniversaryPhotoProps) {
  const { url } = useSignedAssetUrl(assetPath);
  if (url === null) {
    return (
      <span
        className="slide-canvas__anniv-photo-fallback"
        style={{ background: accent, color: inkOn(accent) }}
        aria-hidden="true"
      >
        {initials(title)}
      </span>
    );
  }
  return <img src={url} alt="" draggable={false} />;
});

interface ItemRowsProps {
  items: readonly SlideDraftItem[];
  accent: string;
}

/** The team grid: rows of small discs, name and designation under each. */
export const GridPreview = memo(function GridPreview({ items, accent }: ItemRowsProps) {
  const columns = items.length > GRID_NARROW_MAX_ITEMS ? 3 : 2;
  return (
    <span
      className="slide-canvas__grid"
      style={{
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        columnGap: `${GRID_CELL_GAP_X * 100}cqw`,
        rowGap: `${GRID_CELL_GAP_Y * 100}cqw`,
      }}
      aria-hidden="true"
    >
      {items.map((item, index) => (
        <span className="slide-canvas__grid-cell" key={index}>
          <PortraitDisc
            assetPath={item.asset_path}
            title={item.title}
            accent={accent}
            diameter={GRID_PORTRAIT_DIAMETER}
          />
          <span className="slide-canvas__grid-name" style={{ fontSize: `${GRID_NAME_FONT * 100}cqw` }}>
            {item.title}
          </span>
          {item.subtitle.trim() ? (
            <span
              className="slide-canvas__grid-subtitle"
              style={{ fontSize: `${GRID_SUBTITLE_FONT * 100}cqw` }}
            >
              {item.subtitle}
            </span>
          ) : null}
        </span>
      ))}
    </span>
  );
});

/** The printed-menu rows: dish left, dotted leader, price right. */
export const MenuPreview = memo(function MenuPreview({ items }: { items: readonly SlideDraftItem[] }) {
  return (
    <span className="slide-canvas__menu" style={{ rowGap: `${MENU_ROW_GAP * 100}cqw` }} aria-hidden="true">
      {items.map((item, index) => (
        <span className="slide-canvas__menu-row" key={index}>
          <span className="slide-canvas__menu-line" style={{ fontSize: `${MENU_ROW_TITLE_FONT * 100}cqw` }}>
            <span className="slide-canvas__menu-title">{item.title}</span>
            {item.value.trim() ? (
              <>
                <span className="slide-canvas__menu-leader" />
                <span className="slide-canvas__menu-value">{item.value}</span>
              </>
            ) : null}
          </span>
          {item.subtitle.trim() ? (
            <span
              className="slide-canvas__menu-subtitle"
              style={{ fontSize: `${MENU_ROW_SUBTITLE_FONT * 100}cqw` }}
            >
              {item.subtitle}
            </span>
          ) : null}
        </span>
      ))}
    </span>
  );
});

/** The offer's benefit rows: accent tick, the benefit, an optional value. */
export const OfferPreview = memo(function OfferPreview({ items, accent }: ItemRowsProps) {
  return (
    <span
      className="slide-canvas__offer"
      style={{ fontSize: `${OFFER_ROW_FONT * 100}cqw` }}
      aria-hidden="true"
    >
      {items.map((item, index) => (
        <span className="slide-canvas__offer-row" key={index}>
          <span style={{ color: accent, marginRight: `${OFFER_TICK_GAP * 100}cqw` }}>✓</span>
          <span>
            {item.title}
            {item.value.trim() ? ` · ${item.value.trim()}` : ''}
          </span>
        </span>
      ))}
    </span>
  );
});
